// Match card agreement panel (Lisa, 2026-07-23).
//
// Two jobs, both of which the card page was missing entirely:
//
//  1. SHOW WHO HAS SIGNED. Every one of these facts was already in the
//     database and on the Discord DMs, and none of it was on screen. An admin
//     looking at a card that had not built could not tell whether it was
//     waiting on a captain or blocked by someone who cannot make the time.
//
//  2. LET THEM SIGN HERE. Until now Discord was the ONLY door — a captain who
//     deleted the DM had no way to agree, and an operator could not act for a
//     stuck side without calling the API by hand.
//
// Styling follows the house restraint rule: typography and iconography carry
// the state, not coloured panels or borders. The accent is used for DONE only
// — the same meaning it carries in SetupProgressStrip, so "orange" reads
// consistently across the page rather than as a warning here and a success
// there.
//
// T1b (Lisa, 2026-07-24) — THIS is now where a clan captain's fielded squad
// is picked, not a separate step: this is the moment they are already
// asserting "I'm good with this date/time", so it is also the moment they
// assert "here's who I'm fielding". Reuses MatchRosterInitPanel's
// ClanSeatPicker/toggleSeatSelection/resolveTeamSizeCeiling rather than a
// second copy — the ticked ids are validated the SAME way on both surfaces
// (against that side's registered seats + the comp's teamSize ceiling), so
// there is exactly one picker implementation to keep correct. See
// shared/matchCardAgreement.ts's T1a doc comment for the backend half.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CircleDashed, Clock, X } from 'lucide-react';
import { api, parseApiError, type AgreementParty } from '../lib/api';
import { deriveAgreementView, viewerParty, agreementSummary } from '../lib/cardAgreement';
import { cn } from '../lib/cn';
import { ClanSeatPicker, toggleSeatSelection, resolveTeamSizeCeiling } from './MatchRosterInitPanel';

interface Props {
  matchId: string;
  compId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: any;
  clan1Label: string;
  clan2Label: string;
  scheduledAt?: number;
  viewer: { discordId?: string; runsSide1: boolean; runsSide2: boolean; isOrganiser: boolean };
  /** Resolves a discordId to a display name where the page already knows one. */
  nameFor?: (discordId: string) => string | undefined;
  // T1b (2026-07-24) — the fielded-squad picker attached to a clan captain's
  // own signature needs the same clan-identity + ceiling inputs
  // MatchRosterInitPanel already threads through from the match row (see that
  // component's clan1ClanId/clan1TeamNo doc comments for why these exist
  // separately from clan1Label/clan2Label, which may be a qualified display
  // string GET /clan/:name can't resolve).
  clan1ClanId?: string;
  clan2ClanId?: string;
  clan1TeamNo?: number | null;
  clan2TeamNo?: number | null;
  compTeamSize?: number;
}

export function CardAgreementPanel({
  matchId, compId, match, clan1Label, clan2Label, scheduledAt, viewer, nameFor,
  clan1ClanId, clan2ClanId, clan1TeamNo, clan2TeamNo, compTeamSize,
}: Props) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const view = deriveAgreementView(match, { clan1: clan1Label, clan2: clan2Label });
  const myParty = viewerParty(match, viewer);
  // Which SIDE (not party) myParty resolves to, if any — 'admin' fields no
  // squad, so mySide stays null for it and the picker below never renders.
  const mySide: 1 | 2 | null = myParty === 'clan1' ? 1 : myParty === 'clan2' ? 2 : null;

  const mine = myParty ? view.lines.find(l => l.party === myParty) : undefined;
  const iHaveAnswered = mine?.state === 'agreed';

  // T1b — kills auto-select-all: starts empty, the captain ticks who is
  // actually fielding before the Agree button unlocks (squadFloorMet below).
  const teamSize = resolveTeamSizeCeiling(compTeamSize);
  const myClanKey = mySide === 1 ? (clan1ClanId ?? clan1Label) : mySide === 2 ? (clan2ClanId ?? clan2Label) : undefined;
  const myTeamNo = mySide === 1 ? clan1TeamNo : mySide === 2 ? clan2TeamNo : undefined;
  const [fieldedSquad, setFieldedSquad] = useState<string[]>([]);
  const myClanQuery = useQuery({
    queryKey: ['clan', myClanKey],
    queryFn: () => api.clan(myClanKey as string),
    enabled: !!myClanKey && !iHaveAnswered,
  });
  // A clan party may not agree with an empty field — an untouched picker is
  // not "nobody's playing", it is a picker nobody touched. Admin has no side
  // to field, so the floor never gates them.
  const squadFloorMet = !mySide || fieldedSquad.length >= 1;

  const respond = useMutation({
    // T6 (Lisa, 2026-07-24 — HARD BLOCK): `party` is only ever undefined now
    // — the viewer signing their OWN line. The operator "agree for {clan}"
    // override this used to also drive is gone (see the removed button
    // below): an admin has no squad to supply for a clan side, so that
    // override can no longer complete one either. `party` stays in the type
    // because the backend route still accepts an explicit override for the
    // admin/time party (never had a squad requirement, never needed one),
    // it's just that this component has nothing that sends it any more.
    mutationFn: ({ agree, party }: { agree: boolean; party?: AgreementParty }) =>
      agree
        ? api.cardAgree(matchId, compId, party, (!party && mySide) ? fieldedSquad : undefined)
        : api.cardDisagree(matchId, compId, party),
    onSuccess: () => {
      setError(null);
      // The card BUILDS on the last signature, server-side and asynchronously
      // (the lifecycle lambda owns it), so refetching the card is how this page
      // learns the slots now exist. Invalidate rather than patch the cache: the
      // build changes far more than the agreement fields.
      qc.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
    // parseApiError strips the "(HTTP 400)" apiFetch suffix and surfaces the
    // human message the backend already wrote (Field at least one player… /
    // You can field at most N players… / Not on this side's roster: …) —
    // FIELDED_SQUAD_EMPTY/OVER_CEILING/STRANGER never reach the screen as a
    // raw error, just their message text.
    onError: (e: Error) => setError(parseApiError(e).message || 'That did not go through'),
  });

  // Once the card is built the agreement is history — the panel would only be
  // clutter above a live card.
  if (match.cardStatus && match.cardStatus !== 'DRAFT') return null;

  return (
    <section
      id="match-card-agreement"
      className="rounded-xl border border-iron-700 bg-iron-900 p-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-display text-xs font-bold uppercase tracking-wider text-iron-200">
          Date &amp; time agreement
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-meta">
          {view.agreedCount}/{view.totalCount} agreed
        </span>
      </div>

      <p className="mt-1 font-mono text-[11px] text-iron-500">
        {/* Say what completing this DOES. "Init match card" failing to say so is
            the exact complaint that started this work. */}
        The match card is built — and uploads open — once everyone has agreed the date and time.
      </p>

      {scheduledAt ? (
        <p className="mt-3 flex items-center gap-2 font-display text-sm font-bold text-iron-100">
          <Clock className="h-4 w-4 shrink-0 text-ink-meta" />
          {new Date(scheduledAt).toLocaleString(undefined, {
            weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
          })}
        </p>
      ) : (
        <p className="mt-3 font-mono text-[11px] text-iron-500">No date and time set yet — nothing to agree to.</p>
      )}

      <ul className="mt-3 space-y-1.5">
        {view.lines.map(line => (
          <li key={line.party} className="flex items-center gap-2 text-xs">
            {line.state === 'agreed'
              ? <Check className="h-3.5 w-3.5 shrink-0 text-accent-500" strokeWidth={2.5} />
              : line.state === 'objected'
                ? <X className="h-3.5 w-3.5 shrink-0 text-ink-meta" strokeWidth={2.5} />
                : <CircleDashed className="h-3.5 w-3.5 shrink-0 text-ink-meta" strokeWidth={2} />}
            <span className={cn('text-ink-secondary', line.state === 'agreed' && 'text-ink-primary')}>
              {line.label}
            </span>
            <span className="font-mono text-[10px] text-ink-meta">
              {line.state === 'agreed'
                // An auto-accept is a real outcome but NOT a real answer —
                // never let a timeout read as somebody having said yes.
                ? (line.auto ? 'no reply — auto-accepted at T-2h' : `agreed${line.by && nameFor?.(line.by) ? ` · ${nameFor(line.by)}` : ''}`)
                : line.state === 'objected'
                  ? 'cannot make this time'
                  : 'waiting'}
            </span>
          </li>
        ))}
      </ul>

      <p className="mt-3 font-mono text-[11px] text-iron-500">{agreementSummary(view)}</p>

      {view.blocked && (
        <p className="mt-1 font-mono text-[11px] text-iron-400">
          {/* Say what to DO about it. A blocked card with no stated way forward
              is how "it's broken" tickets get raised. */}
          The match is still on — agree a new time between you, then reschedule it. Everyone will be asked again.
        </p>
      )}

      {myParty && scheduledAt && (
        <div className="mt-4">
          {/* T1b — the fielded squad, picked at the moment of agreeing. Only a
              clan party (mySide set) fields anyone; admin has no side. Hidden
              once answered — the write is write-once server-side, so further
              ticking here would do nothing. */}
          {mySide && !iHaveAnswered && (
            <div className="mb-3 rounded-lg border border-iron-700 bg-iron-950 p-3">
              <p className="mb-2 font-mono text-[11px] uppercase tracking-wider text-iron-500">
                Who&apos;s fielding for {mySide === 1 ? clan1Label : clan2Label}?
              </p>
              <ClanSeatPicker
                clanLabel={mySide === 1 ? clan1Label : clan2Label}
                detail={myClanQuery.data}
                isLoading={myClanQuery.isLoading}
                error={myClanQuery.error}
                teamSize={teamSize}
                maxSquad={teamSize}
                selected={fieldedSquad}
                onToggle={id => setFieldedSquad(prev => toggleSeatSelection(prev, id, teamSize))}
                restrictToTeamNo={myTeamNo ?? null}
              />
              {fieldedSquad.length === 0 && (
                <p className="mt-2 font-mono text-[11px] text-iron-500">
                  Tick at least one player before you can agree — only ticked players get match-card slots.
                </p>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => respond.mutate({ agree: true })}
              disabled={respond.isPending || iHaveAnswered || !squadFloorMet}
              className="rounded-md bg-forge-600 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-forge-500 disabled:opacity-50"
            >
              {iHaveAnswered ? 'You have agreed' : respond.isPending ? 'Sending…' : 'Agree to date & time'}
            </button>
            <button
              onClick={() => respond.mutate({ agree: false })}
              disabled={respond.isPending || mine?.state === 'objected'}
              className="rounded-md border border-iron-700 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-iron-300 hover:border-iron-500 disabled:opacity-50"
            >
              {mine?.state === 'objected' ? 'You cannot make it' : 'Cannot make it'}
            </button>
            {/* T6 (Lisa, 2026-07-24 — HARD BLOCK): the "agree for {clan}"
                operator override that used to live here is REMOVED, not just
                hidden. It only ever rendered for clan1/clan2 lines (the
                filter below always excluded 'admin'), and a clan party can no
                longer agree at all without a valid fieldedSquad — an
                organiser has no squad of their own to supply on a captain's
                behalf, so the override would just 400 FIELDED_SQUAD_REQUIRED
                every time now. A squad-less clan side is resolved by that
                captain picking, never by an admin forcing it. If the
                admin/time party is ever the one left waiting, the organiser
                signs it through their own primary "Agree to date & time"
                button above (myParty resolves to 'admin' for them there) —
                that path never carried a squad and is untouched. */}
          </div>
        </div>
      )}

      {!myParty && (
        <p className="mt-3 font-mono text-[11px] text-iron-500">
          Waiting on the people above — you are not a signatory on this match.
        </p>
      )}

      {error && <p className="mt-2 font-mono text-[11px] text-iron-300">{error}</p>}
    </section>
  );
}
